'use strict'

import { useMemo, useState } from 'react'
import {
  parseYmd, toYmd, isBiWeeklyCode, isLongCycleCode, isDueOnDate
} from './scheduleUtils'

/**
 * 6-stage filtering pipeline for map property features.
 *
 * Accepts raw features + external state needed by the pipeline, returns
 * `filteredFeatures` plus all filter state and setters.
 *
 * @param {{
 *   features: Array, focusDate: string, focusCrewId: string, isFleetMode: boolean, isRouteMode: boolean,
 *   effectiveCrewByProperty: Map, routeStopIds: Set, fleetRouteStopIds: Set,
 *   filteredContextDates: Array, showFleetRoutes: boolean
 * }} deps
 */
export default function useMapFilters ({
  features,
  focusDate,
  focusCrewId,
  isFleetMode,
  effectiveCrewByProperty,
  routeStopIds,
  fleetRouteStopIds,
  filteredContextDates,
  showFleetRoutes
}) {
  // ── Filter state ────────────────────────────────────────────
  const [showVipOnly, setShowVipOnly] = useState(false)
  const [showAnchorsOnly, setShowAnchorsOnly] = useState(false)
  const [showLockedOnly, setShowLockedOnly] = useState(false)
  const [routeFilterId, setRouteFilterId] = useState('')
  const [showDueOnly, setShowDueOnly] = useState(false)
  const [frequencyFocus, setFrequencyFocus] = useState('all') // all | weekly | biweekly
  const [showIrregularMtc, setShowIrregularMtc] = useState(false)
  const [showRouteStops, setShowRouteStops] = useState(true)
  const [showUnassigned, setShowUnassigned] = useState(false)
  const [fleetTodayOnly, setFleetTodayOnly] = useState(true)

  // ── Filtering pipeline ──────────────────────────────────────

  const propertyFeatures = useMemo(() => {
    let props = features.filter(f => f.properties?.featureType === 'property')

    // 1. Attribute filters
    if (showVipOnly) props = props.filter(f => f.properties.isVip)
    if (showAnchorsOnly) props = props.filter(f => f.properties.isAnchor)
    if (showLockedOnly) props = props.filter(f => f.properties.isLocked)

    // Helper: resolve effective crew/route considering date-specific overrides
    const getEffective = (p) => {
      const dateOverride = effectiveCrewByProperty.get(Number(p.aspirePropertyId))
      return {
        routeId: dateOverride?.routeId || p.assignedRouteId || null,
        crewId: dateOverride?.crewId || p.assignedCrewId || null
      }
    }

    // 2. Legend click-filter
    if (routeFilterId === 'unassigned') {
      props = props.filter(f => {
        const eff = getEffective(f.properties)
        return !eff.routeId && !eff.crewId
      })
      return props
    } else if (routeFilterId) {
      props = props.filter(f => {
        const eff = getEffective(f.properties)
        return String(eff.routeId || eff.crewId || '') === String(routeFilterId)
      })
    }

    // 3. Frequency focus
    if (frequencyFocus === 'weekly') {
      props = props.filter(f => !isBiWeeklyCode(f.properties?.frequencyCode, f.properties?.frequency))
    }
    if (frequencyFocus === 'biweekly') {
      props = props.filter(f => isBiWeeklyCode(f.properties?.frequencyCode, f.properties?.frequency))
    }

    // 4. Due in context
    if (showDueOnly && filteredContextDates.length) {
      props = props.filter(f => filteredContextDates.some(date => isDueOnDate(f.properties || {}, date)))
    }

    // 5. Irregular Mtc filter
    if (!showIrregularMtc) {
      props = props.filter(f => {
        const p = f.properties || {}
        if (!isLongCycleCode(p.frequencyCode, p.frequency)) return true
        const visits = Array.isArray(p.scheduledVisitDates) ? p.scheduledVisitDates : []
        if (!visits.length || !filteredContextDates.length) return false
        return filteredContextDates.some(date => {
          const dateStr = toYmd(date)
          return visits.some(v => String(v.date || v || '').slice(0, 10) === dateStr)
        })
      })
    }

    // 6. Mode-dependent visibility
    if (isFleetMode) {
      if (fleetTodayOnly && focusDate) {
        const focusDateObj = parseYmd(focusDate)
        props = props.filter(f => {
          const p = f.properties || {}
          const eff = getEffective(p)
          const isAssigned = !!(eff.routeId || eff.crewId)
          const isUnassignedProp = !eff.routeId && !eff.crewId
          if (isAssigned && showFleetRoutes && fleetRouteStopIds.size > 0) {
            return fleetRouteStopIds.has(Number(p.aspirePropertyId))
          }
          if (isAssigned && isDueOnDate(p, focusDateObj)) return true
          // Unassigned = unassigned, regardless of frequency (no long-cycle exclusion).
          if (showUnassigned && isUnassignedProp) return true
          return false
        })
      } else {
        props = props.filter(f => {
          const p = f.properties || {}
          const eff = getEffective(p)
          const isAssigned = !!(eff.routeId || eff.crewId)
          if (isAssigned) return true
          if (showUnassigned) return true
          return false
        })
      }
    } else if (focusCrewId) {
      if (showRouteStops) {
        props = props.filter(f => {
          const p = f.properties || {}
          const isOnRoute = routeStopIds.size > 0 && routeStopIds.has(Number(p.aspirePropertyId))
          if (isOnRoute) return true
          const eff = getEffective(p)
          const isUnassignedProp = !eff.routeId && !eff.crewId
          if (showUnassigned && isUnassignedProp) return true
          return false
        })
      } else {
        props = props.filter(f => {
          const p = f.properties || {}
          const eff = getEffective(p)
          const isAssigned = !!(eff.routeId || eff.crewId)
          if (isAssigned) return true
          if (showUnassigned) return true
          return false
        })
      }
    }

    return props
  }, [
    effectiveCrewByProperty,
    focusCrewId,
    focusDate,
    fleetTodayOnly,
    fleetRouteStopIds,
    isFleetMode,
    routeFilterId,
    features,
    filteredContextDates,
    frequencyFocus,
    routeStopIds,
    showAnchorsOnly,
    showDueOnly,
    showFleetRoutes,
    showIrregularMtc,
    showLockedOnly,
    showRouteStops,
    showUnassigned,
    showVipOnly
  ])

  const allPropertyFeatures = useMemo(
    () => features.filter(f => f.properties?.featureType === 'property'),
    [features]
  )

  // ── Active filter count (for badge) ─────────────────────────

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (showVipOnly) count++
    if (showAnchorsOnly) count++
    if (showLockedOnly) count++
    if (routeFilterId) count++
    if (showDueOnly) count++
    if (frequencyFocus !== 'all') count++
    if (showIrregularMtc) count++
    return count
  }, [showVipOnly, showAnchorsOnly, showLockedOnly, routeFilterId, showDueOnly, frequencyFocus, showIrregularMtc])

  const resetFilters = () => {
    setShowVipOnly(false)
    setShowAnchorsOnly(false)
    setShowLockedOnly(false)
    setRouteFilterId('')
    setShowDueOnly(false)
    setFrequencyFocus('all')
    setShowIrregularMtc(false)
  }

  return {
    // Filtered output
    propertyFeatures,
    allPropertyFeatures,
    activeFilterCount,
    resetFilters,

    // Filter state + setters
    showVipOnly, setShowVipOnly,
    showAnchorsOnly, setShowAnchorsOnly,
    showLockedOnly, setShowLockedOnly,
    routeFilterId, setRouteFilterId,
    showDueOnly, setShowDueOnly,
    frequencyFocus, setFrequencyFocus,
    showIrregularMtc, setShowIrregularMtc,
    showRouteStops, setShowRouteStops,
    showUnassigned, setShowUnassigned,
    fleetTodayOnly, setFleetTodayOnly
  }
}
